import { Accordion, Box, Card, Chip, Divider, Grid, Stack, Tooltip, Typography } from "@mui/material"
import { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { parseDbStringSeconds, secondsToTimeString } from "../../../../../utils/date"
import AccordionSummaryAverageTime from "./AccordionSummaryAverageTime"
import AccordionDetailsAverageTime from "./AccordionDetailsAverageTime"
import useTrademarkListFilter from "../../../../../hooks/useTrademarkListFilter"

const TIME_CONFIG = {
    tempoConfirmacao: {
        label: 'T. M. confirmação',
        tooltip: 'Tempo entre a data do pedido e data de confirmação',
        order: 1,
    },
    tempoProducao: {
        label: 'T. M. produção',
        tooltip: 'Tempo entre a data de confirmação até a data de retirada ou saída para entrega',
        order: 2,
    },
    tempoEntrega: {
        label: 'T. M. entrega',
        tooltip: 'Tempo entre a saída e entrega',
        order: 3,
    },
    tempoTotal: {
        label: 'T. M. pedido',
        tooltip: 'Tempo total do ciclo do pedido',
        order: 4,
    },
}

export default function AverageTimes() {
    const rawTimeMetrics = useSelector((state) => state.kpiRealTime.timeMetrics) || []
    const orderedKeys = Object.entries(TIME_CONFIG).sort(([, a], [, b]) => a.order - b.order).map(([key]) => key)
    const [expanded, setExpanded] = useState(false)

    // faz o filtro das listas de métricas por marca selecionada pelo usuário
    const filteredRawTimeMetrics = useTrademarkListFilter(rawTimeMetrics)

    const handleChange = (panel) => (__, isExpanded) => {
        setExpanded(isExpanded ? panel : false)
    }

    const processedMetrics = useMemo(() => {
        if (!Array.isArray(filteredRawTimeMetrics) || filteredRawTimeMetrics.length === 0) {
            return {}
        }

        const today = new Date()
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        const yesterdayStart = new Date(todayStart)
        yesterdayStart.setDate(yesterdayStart.getDate() - 1)

        const todayRecords = []
        const yesterdayRecords = []
        const monthRecords = []

        filteredRawTimeMetrics.forEach((item) => {
            const dPedido = new Date(item.dataPedido || item.data_pedido)
            const dCheck = new Date(dPedido.getFullYear(), dPedido.getMonth(), dPedido.getDate())
            const dCheckTime = dCheck.getTime()

            if (dCheckTime === todayStart.getTime()) {
                todayRecords.push(item)
            }

            if (dCheckTime === yesterdayStart.getTime()) {
                yesterdayRecords.push(item)
            }

            if (dCheck >= firstDayOfMonth) {
                monthRecords.push(item)
            }
        })

        const calcAvg = (records, type) => {
            let total = 0
            let count = 0

            records.forEach((r) => {
                const parsed = parseDbStringSeconds(r[type])

                if (parsed !== null && parsed > 0) {
                    total += parsed
                    count++
                } else {
                    let startStr, endStr

                    switch (type) {
                        case 'tempoConfirmacao':
                            startStr = r.dataPedido
                            endStr = r.dataConfirmacao
                            break
                        case 'tempoProducao':
                            startStr = r.dataConfirmacao
                            endStr = r.dataSaida
                            break
                        case 'tempoEntrega':
                            startStr = r.dataSaida
                            endStr = r.dataEntrega
                            break
                        case 'tempoTotal':
                            startStr = r.dataPedido
                            endStr = r.dataEntrega
                            break
                        default:
                            return
                    }

                    if (startStr && endStr) {
                        const startDate = new Date(startStr)
                        const endDate = new Date(endStr)
                        const diffSec = (endDate - startDate) / 1000

                        if (diffSec > 0) {
                            total += diffSec
                            count++
                        }
                    }
                }
            })

            return count ? total / count : 0
        }

        const result = {}

        orderedKeys.forEach((key) => {
            const todayAvg = calcAvg(todayRecords, key)
            const yesterdayAvg = calcAvg(yesterdayRecords, key)
            const monthAvg = calcAvg(monthRecords, key)

            const delta = yesterdayAvg ? ((todayAvg - yesterdayAvg) / yesterdayAvg) * 100 : todayAvg ? 100 : 0

            result[key] = {
                hoje: secondsToTimeString(todayAvg),
                ontem: secondsToTimeString(yesterdayAvg),
                mes: secondsToTimeString(monthAvg),
                delta: Number(delta.toFixed(1)),
            }
        })

        return result
    }, [filteredRawTimeMetrics])

    const unidadesMap = useMemo(() => {
        if (!Array.isArray(filteredRawTimeMetrics)) return {}

        const today = new Date()
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

        return filteredRawTimeMetrics.reduce((acc, item) => {
            const idUnidade = item.idUnidade
            const unidadeName = item.unidadeName
            const dataPedidoStr = item.dataPedido

            if (!idUnidade || !dataPedidoStr) return acc

            const dataPedido = new Date(dataPedidoStr)

            if (dataPedido < firstDayOfMonth) return acc

            if (!acc[idUnidade]) {
                acc[idUnidade] = {
                    unidadeName,
                    pedidos: [],
                }
            }

            acc[idUnidade].pedidos.push(item)

            return acc
        }, {})
    }, [filteredRawTimeMetrics])

    return (

        <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', mb: 2 }}>
                <Typography variant="h3">
                    Tempos Médios
                </Typography>
            </Box>

            <Grid container spacing={1.5}>
                {orderedKeys.map((key) => {
                    const data = processedMetrics[key] || {}
                    const config = TIME_CONFIG[key]

                    return (
                        <Grid item xs={12} md={6} key={key}>
                            <Card
                                key={key}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                }}
                            >
                                <Accordion
                                    expanded={expanded === key}
                                    onChange={handleChange(key)}
                                    sx={{
                                        margin: 0,
                                        padding: 0,
                                        borderRadius: 0,
                                        boxShadow: "none",
                                        background: "none",
                                    }}
                                >
                                    <AccordionSummaryAverageTime
                                        data={data}
                                        config={config}
                                        expanded={expanded === key} />

                                    <Divider />

                                    <AccordionDetailsAverageTime
                                        data={data}
                                        config={config}
                                        unidadesMap={unidadesMap}
                                        metricKey={key} />

                                </Accordion >
                            </Card>
                        </Grid>
                    )
                })}
            </Grid>
        </Box >
    )
}